// 🧪 Demo Company Utilities for Testing
// Allows easy creation and manipulation of demo companies for subscription testing

import { db } from '../firebase.config';
import { 
  doc, 
  setDoc, 
  getDoc, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { createTrialSubscription } from './trialSubscriptionUtils';

/**
 * Create a demo company with customizable subscription
 * @param {string} userId - User ID to own the company
 * @param {Object} options - Configuration options
 * @returns {Object} Created company details
 */
export async function createDemoCompany(userId, options = {}) {
  const {
    companyName = 'Demo Test Company',
    companyId = `demo_${Date.now()}`,
    planId = 'pro',
    planName = 'PRO Plan (Demo)',
    startDate = new Date(),
    endDate = null, // If null, will be calculated from durationDays
    durationDays = 30,
    status = 'active',
    isTrial = false,
    customLimits = null
  } = options;

  try {
    console.log('🧪 Creating demo company:', companyId);

    // Calculate end date if not provided
    const subscriptionEndDate = endDate || new Date(startDate.getTime() + durationDays * 24 * 60 * 60 * 1000);

    // Default limits based on plan
    const defaultLimits = {
      starter: {
        firebase_operations: 3000,
        storage_mb: 100,
        sales_documents: 50,
        parties: 100
      },
      basic: {
        firebase_operations: 10000,
        storage_mb: 300,
        sales_documents: 100,
        parties: 200
      },
      pro: {
        firebase_operations: 50000,
        storage_mb: 1024,
        sales_documents: 500,
        parties: 500
      },
      enterprise: {
        firebase_operations: 200000,
        storage_mb: 5120,
        sales_documents: 10000,
        parties: 10000
      }
    };

    const limits = customLimits || defaultLimits[planId] || defaultLimits.pro;

    // Create company document
    const companyRef = doc(db, 'companies', companyId);
    await setDoc(companyRef, {
      name: companyName,
      createdBy: userId,
      createdAt: serverTimestamp(),
      isDemo: true,
      demoCreatedAt: new Date().toISOString()
    }, { merge: true });

    // Create company info
    const companyInfoRef = doc(db, 'companies', companyId, 'company_info', 'info');
    await setDoc(companyInfoRef, {
      firmName: companyName,
      name: companyName,
      gstin: 'DEMO123456789',
      address: 'Demo Address for Testing',
      phone: '1234567890',
      email: 'demo@test.com',
      isDemo: true
    }, { merge: true });

    // Create subscription
    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    
    const subscriptionData = {
      // Base Plan
      basePlan: {
        planId,
        planName,
        price: isTrial ? 0 : (planId === 'pro' ? 199 : planId === 'basic' ? 49 : 29),
        limits,
        startDate: Timestamp.fromDate(startDate),
        nextResetDate: Timestamp.fromDate(subscriptionEndDate),
        nextBillingDate: Timestamp.fromDate(subscriptionEndDate),
        validityDays: durationDays,
        bonusDays: {
          total: 0,
          source: null,
          appliedOn: null
        },
        isTrial,
        autoRenew: false,
        paymentMethod: 'demo',
        status
      },

      // Top-ups (empty initially)
      topups: [],

      // Total Entitlements
      totalEntitlements: { ...limits },

      // Current Month Usage
      currentMonthUsage: {
        period: `${startDate.toISOString().split('T')[0]} to ${subscriptionEndDate.toISOString().split('T')[0]}`,
        startDate: startDate.toISOString(),
        resetDate: subscriptionEndDate.toISOString(),
        firebase_operations: 0,
        storage_mb: 0,
        sales_documents: 0,
        parties: 0,
        usageBreakdown: {
          fromBasePlan: {
            firebase_operations: 0,
            storage_mb: 0,
            sales_documents: 0,
            parties: 0
          },
          fromTopups: {}
        },
        lastUpdated: new Date().toISOString()
      },

      // Upgrade History
      upgradeHistory: [],

      // Metadata
      createdAt: Timestamp.fromDate(startDate),
      createdBy: userId,
      updatedAt: serverTimestamp(),
      status,
      autoRenew: false,
      
      // Demo specific
      isDemo: true,
      demoNotes: 'Created for testing subscription blocking logic',
      
      // Legacy compatibility
      plan: planId,
      planId,
      endDate: Timestamp.fromDate(subscriptionEndDate),
      startDate: Timestamp.fromDate(startDate)
    };

    await setDoc(subscriptionRef, subscriptionData);

    console.log('✅ Demo company created successfully:', {
      companyId,
      companyName,
      planId,
      endDate: subscriptionEndDate.toISOString(),
      daysRemaining: Math.ceil((subscriptionEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    });

    return {
      success: true,
      companyId,
      companyName,
      subscription: subscriptionData,
      endDate: subscriptionEndDate,
      daysRemaining: Math.ceil((subscriptionEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    };

  } catch (error) {
    console.error('❌ Error creating demo company:', error);
    throw error;
  }
}

/**
 * Quickly expire a company's subscription for testing
 * @param {string} companyId - Company ID
 * @param {number} daysAgo - How many days ago to set expiration (default: 1)
 */
export async function expireSubscription(companyId, daysAgo = 1) {
  try {
    console.log(`🧪 Expiring subscription for company ${companyId} (${daysAgo} days ago)`);

    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);

    if (!subscriptionSnap.exists()) {
      throw new Error('Subscription not found');
    }

    const subscription = subscriptionSnap.data();
    const expiredDate = new Date();
    expiredDate.setDate(expiredDate.getDate() - daysAgo);

    // Update subscription to expired
    await setDoc(subscriptionRef, {
      ...subscription,
      basePlan: {
        ...subscription.basePlan,
        nextResetDate: Timestamp.fromDate(expiredDate),
        nextBillingDate: Timestamp.fromDate(expiredDate),
        status: 'expired'
      },
      endDate: Timestamp.fromDate(expiredDate),
      status: 'expired',
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log('✅ Subscription expired successfully');
    return {
      success: true,
      expiredDate: expiredDate.toISOString(),
      daysExpired: daysAgo
    };

  } catch (error) {
    console.error('❌ Error expiring subscription:', error);
    throw error;
  }
}

/**
 * Extend subscription by X days
 * @param {string} companyId - Company ID
 * @param {number} days - Number of days to extend
 */
export async function extendSubscription(companyId, days) {
  try {
    console.log(`🧪 Extending subscription for company ${companyId} by ${days} days`);

    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);

    if (!subscriptionSnap.exists()) {
      throw new Error('Subscription not found');
    }

    const subscription = subscriptionSnap.data();
    const currentEndDate = subscription.endDate?.toDate() || subscription.basePlan?.nextBillingDate?.toDate() || new Date();
    const newEndDate = new Date(currentEndDate.getTime() + days * 24 * 60 * 60 * 1000);

    // Update subscription
    await setDoc(subscriptionRef, {
      ...subscription,
      basePlan: {
        ...subscription.basePlan,
        nextResetDate: Timestamp.fromDate(newEndDate),
        nextBillingDate: Timestamp.fromDate(newEndDate),
        status: 'active'
      },
      endDate: Timestamp.fromDate(newEndDate),
      status: 'active',
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log('✅ Subscription extended successfully');
    return {
      success: true,
      newEndDate: newEndDate.toISOString(),
      daysRemaining: Math.ceil((newEndDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    };

  } catch (error) {
    console.error('❌ Error extending subscription:', error);
    throw error;
  }
}

/**
 * Set subscription to expire in X days
 * @param {string} companyId - Company ID
 * @param {number} days - Number of days until expiration
 */
export async function setSubscriptionExpiresIn(companyId, days) {
  try {
    console.log(`🧪 Setting subscription for company ${companyId} to expire in ${days} days`);

    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);

    if (!subscriptionSnap.exists()) {
      throw new Error('Subscription not found');
    }

    const subscription = subscriptionSnap.data();
    const newEndDate = new Date();
    newEndDate.setDate(newEndDate.getDate() + days);

    // Update subscription
    await setDoc(subscriptionRef, {
      ...subscription,
      basePlan: {
        ...subscription.basePlan,
        nextResetDate: Timestamp.fromDate(newEndDate),
        nextBillingDate: Timestamp.fromDate(newEndDate),
        status: days > 0 ? 'active' : 'expired'
      },
      endDate: Timestamp.fromDate(newEndDate),
      status: days > 0 ? 'active' : 'expired',
      updatedAt: serverTimestamp()
    }, { merge: true });

    console.log('✅ Subscription expiration date updated');
    return {
      success: true,
      endDate: newEndDate.toISOString(),
      daysRemaining: days
    };

  } catch (error) {
    console.error('❌ Error setting subscription expiration:', error);
    throw error;
  }
}

/**
 * Get subscription status for a company
 * @param {string} companyId - Company ID
 */
export async function getSubscriptionStatus(companyId) {
  try {
    const subscriptionRef = doc(db, 'companies', companyId, 'subscription_details', 'subscription');
    const subscriptionSnap = await getDoc(subscriptionRef);

    if (!subscriptionSnap.exists()) {
      return { exists: false };
    }

    const subscription = subscriptionSnap.data();
    const endDate = subscription.endDate?.toDate() || subscription.basePlan?.nextBillingDate?.toDate();
    const now = Date.now();
    const endTime = endDate ? endDate.getTime() : 0;
    const daysRemaining = endDate ? Math.ceil((endTime - now) / (1000 * 60 * 60 * 24)) : 0;
    const isExpired = endTime < now;

    return {
      exists: true,
      companyId,
      planId: subscription.planId || subscription.basePlan?.planId,
      planName: subscription.planName || subscription.basePlan?.planName,
      status: subscription.status || 'unknown',
      endDate: endDate?.toISOString(),
      daysRemaining,
      isExpired,
      isDemo: subscription.isDemo || false
    };

  } catch (error) {
    console.error('❌ Error getting subscription status:', error);
    throw error;
  }
}

/**
 * List all demo companies
 */
export async function listDemoCompanies() {
  try {
    // This would require a query, but for now we'll return instructions
    console.log('📋 To list demo companies, query Firestore:');
    console.log('   companies collection where isDemo === true');
    return {
      note: 'Query companies collection where isDemo === true'
    };
  } catch (error) {
    console.error('❌ Error listing demo companies:', error);
    throw error;
  }
}

export default {
  createDemoCompany,
  expireSubscription,
  extendSubscription,
  setSubscriptionExpiresIn,
  getSubscriptionStatus,
  listDemoCompanies
};













